import * as RxDialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ReactNode } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { tv } from 'tailwind-variants';

import Text from '../base/Text';
import './Dialog.css';

const modalContainer = tv({
  base: 'contentShow dark:bg-gray-90 shadow-menu z-1 focus:outline-hidden pointer-events-auto flex flex-col rounded-2xl bg-white transition-shadow dark:text-white',
  variants: {
    fullscreenOnMobile: {
      true: 'h-[99svh] w-screen sm:h-auto sm:max-h-[90svh] sm:w-[calc(100vw-40px)]',
      false: 'max-h-[90svh] w-[calc(100vw-40px)]',
    },
    fixedHeightContent: {
      true: '',
      false: '',
    },
    verticalBleed: {
      true: 'py-0',
      false: 'pt-6',
    },
  },
  compoundVariants: [
    {
      fixedHeightContent: true,
      fullscreenOnMobile: false,
      className: 'sm:h-[90svh] 2xl:h-[1000px]',
    },
    {
      fixedHeightContent: true,
      fullscreenOnMobile: true,
      className: 'sm:h-[90svh] 2xl:h-[1000px]',
    },
  ],
  defaultVariants: {
    fullscreenOnMobile: true,
    verticalBleed: false,
  },
});
const horizontalContent = tv({
  base: 'no-scrollbar flex-1 overflow-y-scroll',
  variants: {
    horizontalBleed: {
      true: 'px-0',
      false: 'px-4 pb-4 lg:px-6 lg:pb-6',
    },
  },
  defaultVariants: {
    horizontalBleed: false,
  },
});

export default function Modal({
  children,
  title,
  description,
  maxWidth = 500,
  fixedHeightContent = false,
  trigger,
  open,
  horizontalBleed,
  verticalBleed,
  onOpenChange,
  footer,
  fullscreenOnMobile = false,
  closeOnClickOutside = true,
  overlayClassName,
  contentClassName,
  titleClassName,
  footerClassName,
}: {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  maxWidth?: number; // Width of the modal, default is 500px
  fixedHeightContent?: boolean; // If true the modal will have a fixed height and won't shrink ever
  trigger?: ReactNode; // If you don't pass a trigger, use open/defaultOpen/onOpenChange to make it a controlled Modal
  horizontalBleed?: boolean;
  verticalBleed?: boolean;
  open?: boolean;
  onOpenChange?(open: boolean): void;
  footer?: ReactNode;
  fullscreenOnMobile?: boolean;
  closeOnClickOutside?: boolean;
  /** Overrides default overlay (semi-transparent scrim). */
  overlayClassName?: string;
  /** Appended to dialog content (e.g. transparent shell around custom inner chrome). */
  contentClassName?: string;
  /** Merged onto the visible dialog title row (after defaults). */
  titleClassName?: string;
  /** Overrides the default footer wrapper classes (border, padding). */
  footerClassName?: string;
}) {
  useHotkeys('escape', () => onOpenChange?.(false), {
    preventDefault: true,
  });
  return (
    <RxDialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <RxDialog.Trigger asChild>{trigger}</RxDialog.Trigger>}
      <RxDialog.Portal>
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
          <RxDialog.Overlay
            className={
              overlayClassName ??
              'overlayShow bg-gray-90/50 dark:bg-gray-75/50 pointer-events-auto absolute inset-0 z-0'
            }
          />
          <RxDialog.Content
            id="place-holder"
            className={[
              modalContainer({
                verticalBleed,
                fullscreenOnMobile,
                fixedHeightContent,
              }),
              contentClassName,
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              maxWidth: `${maxWidth}px`,
            }}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onPointerDownOutside={(event) => {
              const noticeHolder = document.getElementById('notice-holder');
              let clickedElement = event.target as HTMLElement | null;
              // Traverse up the DOM tree to check if the clicked element is inside the notice-holder
              while (clickedElement) {
                if (clickedElement === noticeHolder) {
                  // If the clicked element is the notice-holder or a child of it, prevent auto close
                  event.preventDefault();
                  return;
                }
                clickedElement = clickedElement.parentElement;
              }

              if (!closeOnClickOutside) {
                event.preventDefault();
                return false;
              }
            }}
          >
            {title ? (
              <RxDialog.Title
                className={[
                  'mb-3 flex w-full min-w-0 flex-none px-4 lg:mb-5 lg:px-6',
                  titleClassName,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {typeof title === 'string' ? (
                  <Text
                    variant="studio/title/md"
                    className="text-[20px]/[20px]"
                  >
                    {title}
                  </Text>
                ) : (
                  title
                )}
              </RxDialog.Title>
            ) : (
              <VisuallyHidden asChild>
                <RxDialog.Title>Dialog window</RxDialog.Title>
              </VisuallyHidden>
            )}
            {description ? (
              <RxDialog.Description className="-mt-2 mb-3 px-4 lg:mb-5 lg:px-6">
                {typeof description === 'string' ? (
                  <Text
                    variant="studio/body/medium/sm"
                    className="text-gray-60 text-[16px]/[20px]"
                  >
                    {description}
                  </Text>
                ) : (
                  description
                )}
              </RxDialog.Description>
            ) : (
              <VisuallyHidden asChild>
                <RxDialog.Description>
                  This dialog contains additional content.
                </RxDialog.Description>
              </VisuallyHidden>
            )}
            <div className={horizontalContent({ horizontalBleed })}>
              {children}
            </div>
            {footer && (
              <div
                className={
                  footerClassName ??
                  'border-gray-30 dark:border-gray-75 border-t px-4 pb-4 pt-4 lg:px-6 lg:pb-6 lg:pt-6'
                }
              >
                {footer}
              </div>
            )}
          </RxDialog.Content>
        </div>
      </RxDialog.Portal>
    </RxDialog.Root>
  );
}
